<script setup lang="ts">
/**
 * The double materiality matrix.
 *
 * Two axes that mean different things, a threshold on each, and a labelled
 * point per topic. That last part is why this is drawn rather than charted: the
 * whole use of the picture is seeing *which* topic sits where, and a legend
 * beside an unlabelled scatter makes a reader look things up.
 *
 * Impact materiality runs along the bottom and financial materiality up the
 * side. Which way round is not a preference: impact is outward, what the
 * company does to the world, and financial is inward, what the world does to
 * the company. A matrix with them swapped says the opposite of what its author
 * meant, and nothing about the picture would give it away.
 *
 * Drawn as SVG rather than through the chart library, for the same reason the
 * charts are: this has to survive being exported, and an SVG is text that
 * travels with the document.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import type { MaterialitySection } from '@/domain/section'

const { t } = useI18n()

const props = defineProps<{ section: MaterialitySection }>()

/** The drawing's own units. Everything below is in these. */
const SIZE = 100
const PAD = 12

/** The scale both axes run on. */
const MAX = 5

const plotted = computed(() =>
  props.section.entries
    .filter((entry) => entry.topic.trim() !== '')
    .map((entry) => ({
      id: entry.id,
      topic: entry.topic,
      x: PAD + (entry.impact / MAX) * (SIZE - PAD),
      // Upward on the page is downward in SVG coordinates.
      y: SIZE - PAD - (entry.financial / MAX) * (SIZE - PAD),
      /** Material on both axes, which is what puts a topic in the corner. */
      both: entry.impact >= props.section.threshold && entry.financial >= props.section.threshold,
      /**
       * Which side of its dot the label sits.
       *
       * A topic material on impact plots near the right edge, and a label
       * hung to the right of it runs off the drawing. Flipping it is not
       * cosmetic: the topics that matter most are exactly the ones that end up
       * in that corner, so without this the labels that get cut off are the
       * only ones anybody needs to read.
       */
      flipped: entry.impact / MAX > 0.55,
    })),
)

const thresholdX = computed(() => PAD + (props.section.threshold / MAX) * (SIZE - PAD))
const thresholdY = computed(() => SIZE - PAD - (props.section.threshold / MAX) * (SIZE - PAD))
</script>

<template>
  <figure class="matrix">
    <svg
      class="matrix__plot"
      :viewBox="`0 0 ${SIZE} ${SIZE}`"
      role="img"
      :aria-label="section.title"
    >
      <!-- The quadrant that matters, tinted rather than outlined: a topic
           material on both axes is the one the statement has to address. -->
      <rect
        :x="thresholdX"
        y="0"
        :width="SIZE - thresholdX"
        :height="thresholdY"
        class="matrix__quadrant"
      />

      <line :x1="thresholdX" y1="0" :x2="thresholdX" :y2="SIZE - PAD" class="matrix__threshold" />
      <line :x1="PAD" :y1="thresholdY" :x2="SIZE" :y2="thresholdY" class="matrix__threshold" />

      <line :x1="PAD" y1="0" :x2="PAD" :y2="SIZE - PAD" class="matrix__axis" />
      <line :x1="PAD" :y1="SIZE - PAD" :x2="SIZE" :y2="SIZE - PAD" class="matrix__axis" />

      <g v-for="point in plotted" :key="point.id">
        <circle
          :cx="point.x"
          :cy="point.y"
          r="1.9"
          class="matrix__dot"
          :class="{ 'matrix__dot--both': point.both }"
        />
        <text
          :x="point.flipped ? point.x - 3 : point.x + 3"
          :y="point.y + 1.2"
          class="matrix__topic"
          :class="{ 'matrix__topic--flipped': point.flipped }"
        >
          {{ point.topic }}
        </text>
      </g>
    </svg>

    <p class="matrix__axes">
      <span>{{ t('inspector.materiality.impactAxis') }}</span>
      <span>{{ t('inspector.materiality.financialAxis') }}</span>
    </p>

    <figcaption v-if="section.caption" class="page-caption">{{ section.caption }}</figcaption>
  </figure>
</template>

<style scoped>
.matrix {
  margin: 3mm 0 4mm;
}

.matrix__plot {
  display: block;
  width: 100%;
  height: auto;
  /* Square, because both axes run on the same scale and a stretched matrix
     makes a topic look more material on one of them than it is. */
  aspect-ratio: 1;
}

.matrix__quadrant {
  fill: var(--page-accent);
  opacity: 0.07;
}

.matrix__threshold {
  stroke: var(--page-accent);
  stroke-width: 0.4;
  stroke-dasharray: 1.6 1.4;
  opacity: 0.7;
}

.matrix__axis {
  stroke: var(--paper-ink-soft);
  stroke-width: 0.4;
}

.matrix__dot {
  fill: var(--paper-ink-soft);
}

.matrix__dot--both {
  fill: var(--page-accent);
}

/* A halo of the paper's own colour behind each label. Topics cluster on the
   threshold, which is exactly where the dashed lines are, so without it the
   labels that most need reading are the ones with a line through them. */
.matrix__topic {
  fill: var(--paper-ink);
  stroke: var(--paper);
  stroke-width: 1.1;
  paint-order: stroke fill;
  font-family: var(--font-document);
  font-size: 3.2px;
}

.matrix__topic--flipped {
  text-anchor: end;
}

/* The axes named under the plot rather than rotated up its side: rotated type
   at this size is unreadable in print, and the two labels in order say which
   is which without it. */
.matrix__axes {
  display: flex;
  justify-content: space-between;
  gap: 4mm;
  margin: 1.5mm 0 0;
  color: var(--paper-ink-soft);
  font-size: 8pt;
  text-align: left;
}

@media print {
  .matrix__quadrant,
  .matrix__dot--both {
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
}
</style>
